import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import {
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock,
  FaWhatsapp, FaCheckCircle, FaStar, FaTools,
  FaTruck, FaHardHat, FaCubes
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { seoContent } from '../config/seoContent';
import { siteInfo } from '../config/theme';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { AOS.init({ duration: 1000, once: true }); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };

  const services = [
    'Building Demolition', 'Earthwork & Excavation', 'Building Materials Supply',
    'Excavator Breaker Rent', 'Mini Road Roller Rent', 'Soil Compactor Rent',
    'Excavator Rent', 'JCB Rent', 'JCB Breaker Rent',
    'Tipper Rent', 'Open Platform Truck Rent', 'Bobcat Rent',
    'M Sand Supply', 'P Sand Supply', 'Wet Mix Macadam',
    'Coarse Aggregates', 'Red Soil', 'Gravel Filling', 'Other'
  ];

  const reasons = [
    'Free site inspection & no-obligation quote',
    'Response within 2 hours guaranteed',
    '15+ years experience in Coimbatore',
    'Transparent pricing — no hidden charges',
    'Licensed, insured & safety-certified team',
    '300+ satisfied clients across Coimbatore',
  ];

  return (
    <>
      <SEO
        title={seoContent.contact.title}
        description={seoContent.contact.description}
        keywords={seoContent.contact.keywords}
      />

      <div className="page-header contact-header">
        <div className="container">
          <h1 data-aos="fade-up">Contact Us</h1>
          <p data-aos="fade-up" data-aos-delay="100">Get a Free Consultation and Quote — We Respond Within 2 Hours</p>
        </div>
      </div>

      {/* Quick Contact Buttons */}
      <section className="quick-contact-section">
        <div className="container">
          <div className="quick-contact-grid">
            <a href="tel:9894065756" className="quick-contact-card qc-green" data-aos="fade-up">
              <div className="qc-icon"><FaPhone /></div>
              <div><strong>Call Us</strong><span>9894065756</span></div>
            </a>
            <a href="https://wa.me/919894065756" className="quick-contact-card qc-wa" target="_blank" rel="noopener noreferrer" data-aos="fade-up" data-aos-delay="100">
              <div className="qc-icon"><FaWhatsapp /></div>
              <div><strong>WhatsApp</strong><span>Chat Now</span></div>
            </a>
            <a href={`mailto:${siteInfo.email}`} className="quick-contact-card qc-blue" data-aos="fade-up" data-aos-delay="200">
              <div className="qc-icon"><FaEnvelope /></div>
              <div><strong>Email Us</strong><span>{siteInfo.email}</span></div>
            </a>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info" data-aos="fade-right">
              <div className="contact-details">
                <div className="contact-intro">
                  <h2>Get In Touch</h2>
                  <p>Have a question or need a quote? Contact us today. We offer free site inspection for all projects in Coimbatore and respond within 2 hours.</p>
                </div>

                <div className="contact-item">
                  <div className="contact-icon"><FaPhone /></div>
                  <div>
                    <h4>Phone / WhatsApp</h4>
                    <a href="tel:9894065756">9894065756</a>
                    <p style={{fontSize:'13px',color:'var(--text-light)',marginTop:'4px'}}>Mon–Sat: 8:00 AM – 6:00 PM</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon"><FaEnvelope /></div>
                  <div>
                    <h4>Email</h4>
                    <a href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a>
                    <p style={{fontSize:'13px',color:'var(--text-light)',marginTop:'4px'}}>We reply within 24 hours</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon"><FaMapMarkerAlt /></div>
                  <div>
                    <h4>Location</h4>
                    <p>{siteInfo.location}</p>
                    <p style={{fontSize:'13px',color:'var(--text-light)',marginTop:'4px'}}>Serving all Coimbatore areas</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon"><FaClock /></div>
                  <div>
                    <h4>Working Hours</h4>
                    <p>Mon – Sat: 8:00 AM – 6:00 PM</p>
                    <p>Sunday: Emergency calls only</p>
                  </div>
                </div>

                <div className="contact-reasons">
                  <h4>Why Contact Us?</h4>
                  {reasons.map((r, i) => (
                    <div key={i} className="contact-reason-item">
                      <FaCheckCircle /> <span>{r}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="contact-form-wrapper" data-aos="fade-left">
              {submitted ? (
                <div className="form-success">
                  <FaCheckCircle />
                  <h3>Message Sent Successfully!</h3>
                  <p>Thank you for contacting SRI KRISH CIVIL SUPPLIER. Our team will get back to you within 2 hours.</p>
                  <a href="tel:9894065756" className="btn btn-primary"><FaPhone /> Call Us Now</a>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <h3>Send Us a Message</h3>
                  <p style={{color:'var(--text-light)',marginBottom:'25px',fontSize:'15px'}}>
                    Fill out the form and we'll get back to you within 2 hours with a free quote.
                  </p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name *</label>
                      <input type="text" name="name" placeholder="Enter your name" value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input type="tel" name="phone" placeholder="Enter your phone" value={formData.phone} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email Address</label>
                    <input type="email" name="email" placeholder="Enter your email (optional)" value={formData.email} onChange={handleChange} />
                  </div>

                  <div className="form-group">
                    <label>Service Required *</label>
                    <select name="service" value={formData.service} onChange={handleChange} required>
                      <option value="">Select a service</option>
                      {services.map((s, i) => <option key={i} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Your Message *</label>
                    <textarea name="message" placeholder="Describe your project or requirement..." rows="5" value={formData.message} onChange={handleChange} required></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary btn-full">Send Message and Get Free Quote</button>
                  <p className="form-note"><FaCheckCircle /> We respond within 2 hours. No spam, ever.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Quick Reference */}
      <section className="contact-services-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Services We Offer in Coimbatore</h2>
            <p>Contact us for any of these services — free quote guaranteed</p>
          </div>
          <div className="contact-services-grid">
            <div className="cs-card" data-aos="fade-up">
              <FaHardHat />
              <h3>Civil Services</h3>
              <ul>
                <li>Building Demolition</li>
                <li>Earthwork and Excavation</li>
                <li>Site Preparation</li>
                <li>Foundation Work</li>
              </ul>
            </div>
            <div className="cs-card" data-aos="fade-up" data-aos-delay="100">
              <FaTools />
              <h3>Equipment Rental</h3>
              <ul>
                <li>JCB and Excavator Hire</li>
                <li>Tipper and Truck Rental</li>
                <li>Road Roller and Compactor</li>
                <li>Bobcat and Breaker Hire</li>
              </ul>
            </div>
            <div className="cs-card" data-aos="fade-up" data-aos-delay="200">
              <FaCubes />
              <h3>Building Materials</h3>
              <ul>
                <li>M Sand and P Sand</li>
                <li>Coarse Aggregates</li>
                <li>Wet Mix Macadam</li>
                <li>Red Soil and Gravel</li>
              </ul>
            </div>
            <div className="cs-card" data-aos="fade-up" data-aos-delay="300">
              <FaTruck />
              <h3>Transport</h3>
              <ul>
                <li>Material Delivery</li>
                <li>Tipper Hire</li>
                <li>Open Platform Truck</li>
                <li>Bulk Transport</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Ratings Banner */}
      <section className="contact-ratings-section">
        <div className="container">
          <div className="ratings-banner" data-aos="zoom-in">
            <div className="rating-item">
              <div className="rating-stars">{[...Array(5)].map((_, i) => <FaStar key={i} />)}</div>
              <strong>5.0 Rating</strong>
              <span>Google Reviews</span>
            </div>
            <div className="rating-divider"></div>
            <div className="rating-item">
              <strong>300+</strong>
              <span>Happy Clients</span>
            </div>
            <div className="rating-divider"></div>
            <div className="rating-item">
              <strong>500+</strong>
              <span>Projects Done</span>
            </div>
            <div className="rating-divider"></div>
            <div className="rating-item">
              <strong>15+</strong>
              <span>Years in Coimbatore</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
